import React, { useEffect, useState, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { Box, Text, Button, Spinner, Image, Portal } from "@chakra-ui/react";
import EnrollmentList from "@root/components/enrollment-list/enrollmentlist";
import { useTranslation } from "react-i18next";
import ReactVideoPlayerNew from "./ReactVideoPlayerNew";
import { useCourseManagement } from "@root/hooks/useCourseManagement";
import { COURSE_ENROLLMENT_STATUS } from "@root/constants";
import { useSelector } from "react-redux";
import { getLoggedInUser } from "@root/store/selectors/entities";
import { useCourseManagementContext } from "@root/context/course-management-context";
import { useNotification } from "@root/hooks/useNotification";
import { CourseService } from "@root/services/course";
import useMediaQ from "@root/hooks/useMediaQ";
import defaultThumbnail from "@root/assets/images/Course Thunbnail.png";
import CourseHeader from "./CourseHeader";
import ChaptersList from "./ChaptersList";
import { useJournalistCourseDetailTourGuide } from "@root/hooks/useJournalistCourseDetailTourGuide";
import { TourGuide, RestartTourButton } from "@root/components/tour-guide";

const courseService = new CourseService();

const CourseDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { t } = useTranslation("common");
  const [enrolled, setEnrolled] = useState(false);
  const [selectedChapter, setSelectedChapter] = useState(0);
  const [selectedVideoUrl, setSelectedVideoUrl] = useState<string | null>(null);
  // Use ref for synchronous access to progress IDs (avoids async state update issues)
  const progressIdMapRef = useRef<{ [key: number]: number }>({});
  const [hasPlayed, setHasPlayed] = useState(false);
  const [selectedVideoId, setSelectedVideoId] = useState<number | null>(null);
  const loggedInUser: any = useSelector(getLoggedInUser);
  const { showNotification } = useNotification();
  const { sm } = useMediaQ();
  const isMobile = !sm;
  const progressSaveIntervalRef = useRef<NodeJS.Timeout | null>(null);
  const lastSavedProgressRef = useRef<number | null>(null);
  const isSavingProgressRef = useRef<boolean>(false); // Prevent concurrent API calls
  const lastApiCallTimeRef = useRef<number>(0); // Track last API call time for throttling
  const { journalistCourseDetailTourGuide, isMobile: isMobileTour } =
    useJournalistCourseDetailTourGuide();
  const {
    fetchCourses,
    coursesData,
    getCoursesLoading: recommendedCoursesLoading,
  } = useCourseManagementContext();
  const {
    courseChaptersData,
    getCourseChapters,
    fetchCourse,
    courseData: rawCourseData,
    getCoursesLoading,
    uploadCourseEnrollData,
    addCourseEnrollLoading,
    watchLaterLoading,
    uploadCourseProgressData,
    updateCourseEnrollData,
    getCourseProgressById,
    courseProgressData,
    getCourseProgressLoading,
    videoData,
    fetchVideoUrlById,
  } = useCourseManagement();
  const courseData: any = rawCourseData;

  // Check if course enrollment is expired
  const isCourseExpired = courseData?.isCourseExpire === true;
  console.log(courseData);

  useEffect(() => {
    if (id) {
      fetchCourse(Number(id));
      getCourseProgressById(Number(id));
      getCourseChapters(Number(id), { isSignedUrl: true });
      fetchCourses({ recommended: true });
    }
  }, [id]);

  // Initialize progressIdMapRef when courseProgressData is loaded
  useEffect(() => {
    if (courseProgressData?.[0]?.length > 0) {
      console.log("📊 [PROGRESS INIT] Course progress data loaded:", courseProgressData[0]);
      courseProgressData[0].forEach((item: any) => {
        if (item?.video?.id && item?.id) {
          progressIdMapRef.current[item.video.id] = item.id;
          console.log(`📊 [PROGRESS INIT] Mapped video ${item.video.id} -> progress ID ${item.id}, progress: ${item.progress}%, completed: ${item.completed}`);
        }
      });
    }
  }, [courseProgressData]);

  // Initialize lastSavedProgressRef from saved progress when video is selected or progress data loads
  useEffect(() => {
    if (selectedVideoId && courseProgressData?.[0] && courseChaptersData) {
      console.log(`🔄 [PROGRESS INIT] Checking progress for video ID: ${selectedVideoId}`);
      // Get saved progress inline (same logic as getSavedProgress)
      let selectedVideo: any = null;
      for (const chapter of (courseChaptersData as any)?.rows || []) {
        const video = chapter?.courseVideos?.find(
          (v: any) => v?.id === selectedVideoId,
        );
        if (video) {
          selectedVideo = video;
          break;
        }
      }

      const videoIdForPayload =
        selectedVideo?.isFree === false ? selectedVideo?.id : selectedVideoId;

      console.log(`🔄 [PROGRESS INIT] Video ID for payload: ${videoIdForPayload} (isFree: ${selectedVideo?.isFree})`);

      const progressItem = courseProgressData[0].find(
        (item: any) => item?.video?.id === videoIdForPayload,
      );

      console.log(`🔄 [PROGRESS INIT] Found progress item:`, progressItem);

      if (progressItem) {
        // If video is completed, always use 100% progress
        if (progressItem.completed === true) {
          console.log(`✅ [PROGRESS INIT] Video is completed, setting to 100%`);
          lastSavedProgressRef.current = 100;
        } else {
          // Always update lastSavedProgressRef from saved progress if it exists
          const savedProgress = typeof progressItem.progress === "number"
            ? progressItem.progress
            : parseFloat(progressItem.progress || "0");

          console.log(`📈 [PROGRESS INIT] Raw progress value: ${progressItem.progress}, parsed: ${savedProgress}, type: ${typeof progressItem.progress}`);

          if (!Number.isNaN(savedProgress)) {
            // Never reset progress - use saved progress or minimum 0
            const finalProgress = Math.max(0, Math.min(100, savedProgress));
            lastSavedProgressRef.current = finalProgress;
            console.log(`✅ [PROGRESS INIT] Setting lastSavedProgressRef to: ${finalProgress}%`);
          } else if (progressItem.id) {
            // Progress record exists but progress is invalid - use 0 in ref only
            // Don't update the API record unless user actually watches
            console.log(`⚠️ [PROGRESS INIT] Progress is NaN, setting to 0`);
            lastSavedProgressRef.current = 0;
          }
        }
      } else {
        // No progress record exists - reset ref to null so we can create one if needed
        console.log(`❌ [PROGRESS INIT] No progress record found for video ${videoIdForPayload}`);
        lastSavedProgressRef.current = null;
      }
    }
  }, [selectedVideoId, courseProgressData, courseChaptersData]);

  // Fetch video URL by id if selected video is not free
  useEffect(() => {
    if (selectedVideoId != null && selectedVideoUrl != null) {
      // Find isFree for selected video
      let isFree = true;
      for (const chapter of (courseChaptersData as any)?.rows || []) {
        const video = chapter?.courseVideos?.find(
          (v: any) => v?.id === selectedVideoId,
        );
        if (video) {
          isFree = !!video?.isFree;
          break;
        }
      }
      if (!isFree) {
        fetchVideoUrlById(selectedVideoId);
      }
    }
  }, [selectedVideoId, selectedVideoUrl, courseChaptersData]);

  useEffect(() => {
    if (courseData?.isCourseTaken?.id && enrolled) {
      const currentTime = new Date().toISOString();
      updateCourseEnrollData(Number(id), courseData?.isCourseTaken?.id, {
        lastAccessedAt: currentTime,
      });
    }
  }, [courseData?.isCourseTaken?.id, enrolled, id]);
  useEffect(() => {
    if (
      enrolled &&
      !selectedVideoUrl &&
      (courseChaptersData as any)?.rows?.length > 0
    ) {
      setSelectedVideoUrl(
        (courseChaptersData as any)?.rows[0]?.courseVideos?.[0]?.url || null,
      );
      setSelectedVideoId(
        (courseChaptersData as any)?.rows[0]?.courseVideos?.[0]?.id || null,
      );
    }
  }, [enrolled, courseChaptersData, selectedVideoUrl]);

  useEffect(() => {
    if (courseData && loggedInUser) {
      if (
        courseData?.isCourseTaken?.status ===
        COURSE_ENROLLMENT_STATUS.ENROLLED ||
        courseData?.isCourseTaken?.status === COURSE_ENROLLMENT_STATUS.COMPLETED
      ) {
        setEnrolled(true);
      } else {
        setEnrolled(false);
      }
    }
  }, [courseData, loggedInUser]);

  // Save progress when user leaves the page or component unmounts
  useEffect(() => {
    const handleBeforeUnload = () => {
      // Save progress synchronously before page unload
      if (enrolled && selectedVideoId && lastSavedProgressRef.current !== null) {
        // Use a synchronous approach - the progress should already be saved
        // via periodic saves and pause handler, but we'll try one more time
        handleVideoProgress(lastSavedProgressRef.current, false).catch(
          console.error,
        );
      }
    };

    const handleVisibilityChange = () => {
      // Save progress when tab becomes hidden (user switches tabs or minimizes)
      if (document.hidden && enrolled && selectedVideoId && lastSavedProgressRef.current !== null) {
        handleVideoProgress(lastSavedProgressRef.current, false).catch(
          console.error,
        );
      }
    };

    window.addEventListener("beforeunload", handleBeforeUnload);
    document.addEventListener("visibilitychange", handleVisibilityChange);

    return () => {
      window.removeEventListener("beforeunload", handleBeforeUnload);
      document.removeEventListener("visibilitychange", handleVisibilityChange);
      // Save progress on unmount
      if (enrolled && selectedVideoId && lastSavedProgressRef.current !== null) {
        handleVideoProgress(lastSavedProgressRef.current, false).catch(
          console.error,
        );
      }
      // Clear interval on unmount
      if (progressSaveIntervalRef.current) {
        clearInterval(progressSaveIntervalRef.current);
        progressSaveIntervalRef.current = null;
      }
    };
  }, [
    enrolled,
    selectedVideoId,
    courseData,
    courseChaptersData,
    selectedChapter,
    courseProgressData,
    loggedInUser,
  ]);

  if (getCoursesLoading)
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        minH="200px"
      >
        <Spinner size="xl" color="#1819B4" />
      </Box>
    );
  if (!courseData) return <Text>{t("courseDetail.notFound")}</Text>;

  // Define chapters early so it can be used in functions below
  const chapters = (courseChaptersData as any)?.rows || [];

  const progressLookup: { [key: number]: boolean } = {};
  courseProgressData?.[0]?.forEach((item: any) => {
    if (item?.video && item?.video?.id) {
      progressLookup[item?.video?.id] = item?.completed === true;
    }
  });

  const currentVideoUrl =
    enrolled && chapters[selectedChapter]?.courseVideos?.[0]?.url
      ? chapters[selectedChapter]?.courseVideos?.[0]?.url
      : null;

  const handleEnroll = async () => {
    // Check if course enrollment is expired
    if (isCourseExpired) {
      showNotification({
        type: "error",
        message: t("courseDetail.enrollmentExpired"),
      });
      return;
    }

    try {
      if (courseData?.isCourseTaken && courseData?.isCourseTaken?.id) {
        await updateCourseEnrollData(
          Number(id),
          courseData?.isCourseTaken?.id,
          {
            organizationMembershipId: loggedInUser?.id,
            status: COURSE_ENROLLMENT_STATUS.ENROLLED,
            progress: 0,
            isWatchLater: courseData?.isCourseTaken?.isWatchLater,
          },
        );
      } else {
        await uploadCourseEnrollData(Number(id), {
          organizationMembershipId: loggedInUser?.id,
          status: COURSE_ENROLLMENT_STATUS.ENROLLED,
          progress: 0,
        });
      }
      setEnrolled(true);
      fetchCourse(Number(id));
    } catch (error) {
      console.error("Enrollment error:", error);
      showNotification({
        type: "error",
        message: t("courseDetail.enrollError"),
      });
    }
  };

  const handleWatchLater = async () => {
    try {
      // Check if enrollment already exists
      if (courseData?.isCourseTaken && courseData?.isCourseTaken?.id) {
        // Update existing enrollment
        await updateCourseEnrollData(
          Number(id),
          courseData?.isCourseTaken?.id,
          {
            isWatchLater: true,
            status: COURSE_ENROLLMENT_STATUS.WATCHLATER,
          },
          true, // isWatchLater flag
        );
      } else {
        // Create new enrollment
        await uploadCourseEnrollData(
          Number(id),
          {
            organizationMembershipId: loggedInUser?.id,
            isWatchLater: true,
            status: COURSE_ENROLLMENT_STATUS.WATCHLATER,
          },
          true,
        ); // isWatchLater flag
      }
      fetchCourse(Number(id));
    } catch (error) {
      showNotification({
        type: "error",
        message: t("courseDetail.enrollError"),
      });
    }
  };

  const handleUndoWatchLater = async () => {
    try {
      await updateCourseEnrollData(
        Number(id),
        courseData?.isCourseTaken?.id,
        {
          isWatchLater: false,
        },
        true,
      ); // isWatchLater flag
      fetchCourse(Number(id));
    } catch (error) {
      showNotification({
        type: "error",
        message: t("courseDetail.enrollError"),
      });
    }
  };
  const handleUpdateWatchLater = async () => {
    try {
      await updateCourseEnrollData(
        Number(id),
        courseData?.isCourseTaken?.id,
        {
          isWatchLater: true,
          status: COURSE_ENROLLMENT_STATUS.WATCHLATER,
        },
        true,
      ); // isWatchLater flag
      fetchCourse(Number(id));
    } catch (error) {
      showNotification({
        type: "error",
        message: t("courseDetail.enrollError"),
      });
    }
  };

  const getTotalVideos = () => {
    const chapters = (courseChaptersData as any)?.rows || [];
    return chapters.reduce((total: number, chapter: any) => {
      return total + (chapter?.courseVideos?.length || 0);
    }, 0);
  };

  const handleVideoProgress = async (
    progress: number,
    completed: boolean = false,
    skipIfSame: boolean = false,
  ) => {
    // Prevent concurrent API calls - if one is in progress, skip this call
    if (isSavingProgressRef.current) {
      console.log(`⏸️ [PROGRESS SAVE] Skipping - save already in progress`);
      return;
    }

    // Throttle API calls - don't call more than once every 3 seconds
    const now = Date.now();
    const timeSinceLastCall = now - lastApiCallTimeRef.current;
    if (!completed && timeSinceLastCall < 3000) {
      console.log(`⏸️ [PROGRESS SAVE] Throttled - only ${timeSinceLastCall}ms since last call`);
      return;
    }

    // Get chapters from courseChaptersData (may not be initialized yet)
    const chapters = (courseChaptersData as any)?.rows || [];

    // Find selected video object
    let selectedVideo: any = null;
    for (const chapter of chapters) {
      const video = chapter?.courseVideos?.find(
        (v: any) => v?.id === selectedVideoId,
      );
      if (video) {
        selectedVideo = video;
        break;
      }
    }

    // Use selectedVideo.id in payload if not free, else selectedVideoId
    const videoIdForPayload =
      selectedVideo?.isFree === false ? selectedVideo?.id : selectedVideoId;

    // Always check for existing progress record first
    const progressItem = courseProgressData?.[0]?.find(
      (item: any) => item?.video?.id === videoIdForPayload,
    );
    let id = progressIdMapRef.current[videoIdForPayload] || progressItem?.id;

    // CRITICAL: If video is already completed, never update progress again
    if (progressItem?.completed === true) {
      // Video is already completed - don't update progress, keep it at 100%
      lastSavedProgressRef.current = 100;
      return;
    }

    // CRITICAL: Never reset progress to 0 if there's existing progress
    // Only allow progress to increase or stay the same, never decrease
    if (progressItem && typeof progressItem.progress === "number") {
      const existingProgress = progressItem.progress;
      // If trying to set progress lower than existing, use existing progress instead
      if (progress < existingProgress && !completed) {
        progress = existingProgress;
      }
      // If existing progress is already 100%, don't update unless marking as completed
      if (existingProgress >= 100 && !completed) {
        return;
      }
    }

    // Skip if progress hasn't changed significantly (avoid unnecessary API calls)
    if (skipIfSame && lastSavedProgressRef.current !== null) {
      const diff = Math.abs(progress - lastSavedProgressRef.current);
      if (diff < 1) {
        // Less than 1% change, skip saving
        return;
      }
    }

    // Only mark as completed when progress reaches 100%
    const shouldMarkCompleted = progress >= 100 || completed;

    const payload = {
      memberId: loggedInUser?.id,
      courseId: courseData?.id,
      chapterId: chapters[selectedChapter]?.id,
      videoId: videoIdForPayload,
      progress: Math.min(100, Math.max(0, progress)), // Clamp between 0-100
      completed: shouldMarkCompleted,
      lastAccessedAt: new Date().toISOString(),
    };

    // Set flag to prevent concurrent calls
    isSavingProgressRef.current = true;
    lastApiCallTimeRef.current = Date.now();

    try {
      console.log(`💾 [PROGRESS SAVE] Saving progress ${progress}% for video ${videoIdForPayload}, ID: ${id}`);

      if (!id) {
        // Before creating a new record, double-check that one doesn't exist
        // This prevents duplicate records if courseProgressData hasn't loaded yet
        if (!courseProgressData?.[0] || courseProgressData[0].length === 0) {
          // Data not loaded yet - refresh it first
          await getCourseProgressById(courseData?.id);
          // Check again after refresh
          const refreshedProgressItem = courseProgressData?.[0]?.find(
            (item: any) => item?.video?.id === videoIdForPayload,
          );
          id = refreshedProgressItem?.id || progressIdMapRef.current[videoIdForPayload];
        }

        if (!id) {
          // Still no record found - create a new one
          const res = await uploadCourseProgressData(payload);
          const newId = res?.data?.data?.id;
          if (newId) {
            progressIdMapRef.current[videoIdForPayload] = newId;
            await getCourseProgressById(courseData?.id);
          }
        } else {
          // Found existing record after refresh - update it instead
          await courseService?.patchCourseTakerProgress(id, payload);
        }
      } else {
        // Update existing record
        await courseService?.patchCourseTakerProgress(id, payload);
      }

      // Update last saved progress
      lastSavedProgressRef.current = progress;
      console.log(`✅ [PROGRESS SAVE] Successfully saved progress ${progress}%`);

      // Update course-level progress based on all video progress percentages
      if (enrolled && courseData?.isCourseTaken?.id) {
        // Recalculate course progress from all video progress
        const totalVideos = getTotalVideos();
        if (totalVideos > 0) {
          // Get all video progress and calculate average
          const allVideoProgress = courseProgressData?.[0] || [];
          const videoProgressMap = new Map<number, number>();

          // Update current video progress in the map. A video being completed
          // counts as 100% even if the raw progress value is slightly below 100.
          videoProgressMap.set(
            videoIdForPayload,
            shouldMarkCompleted ? 100 : progress,
          );

          // Add all other video progress. Treat completed videos as 100% so the
          // course-level aggregate matches the in-course view (and the server's
          // authoritative recompute), instead of reading a numeric progress that
          // may have been left below 100 when the video was marked complete.
          allVideoProgress.forEach((item: any) => {
            const vidId = item?.video?.id;
            if (vidId && vidId !== videoIdForPayload) {
              const rawVidProgress = typeof item?.progress === "number"
                ? item.progress
                : parseFloat(item?.progress || "0");
              const vidProgress =
                item?.completed === true ? 100 : rawVidProgress;
              if (!Number.isNaN(vidProgress)) {
                videoProgressMap.set(vidId, Math.max(0, Math.min(100, vidProgress)));
              }
            }
          });

          // Calculate total progress: sum of all video progress / total videos
          let totalProgressSum = 0;
          const chapters = (courseChaptersData as any)?.rows || [];
          chapters.forEach((chapter: any) => {
            (chapter?.courseVideos || []).forEach((video: any) => {
              if (video?.id) {
                const videoProgress = videoProgressMap.get(video.id) || 0;
                totalProgressSum += videoProgress;
              }
            });
          });

          const calculatedCourseProgress = totalProgressSum / totalVideos;
          const newProgress = Math.min(100, Math.max(0, calculatedCourseProgress));

          const updatePayload: any = {
            progress: Math.round(newProgress),
            organizationMembershipId: loggedInUser?.id,
            lastAccessedAt: new Date().toISOString(),
          };

          // Mark as completed if all videos are 100%
          if (newProgress >= 100) {
            updatePayload.status = COURSE_ENROLLMENT_STATUS.COMPLETED;
          }

          updateCourseEnrollData(
            Number(id),
            courseData?.isCourseTaken?.id,
            updatePayload,
          );
        }
      }
    } catch (error) {
      console.error("❌ [PROGRESS SAVE] Error saving video progress:", error);
    } finally {
      // Always reset the flag, even if there was an error
      isSavingProgressRef.current = false;
    }
  };

  // Get saved progress for selected video
  const getSavedProgress = (): number | null => {
    console.log(`🔍 [GET SAVED PROGRESS] Called for video ID: ${selectedVideoId}`);
    console.log(`🔍 [GET SAVED PROGRESS] courseProgressData:`, courseProgressData);

    if (!selectedVideoId || !courseProgressData?.[0]) {
      console.log(`❌ [GET SAVED PROGRESS] No selectedVideoId or courseProgressData, returning null`);
      return null;
    }

    let selectedVideo: any = null;
    for (const chapter of (courseChaptersData as any)?.rows || []) {
      const video = chapter?.courseVideos?.find(
        (v: any) => v?.id === selectedVideoId,
      );
      if (video) {
        selectedVideo = video;
        break;
      }
    }

    const videoIdForPayload =
      selectedVideo?.isFree === false ? selectedVideo?.id : selectedVideoId;

    console.log(`🔍 [GET SAVED PROGRESS] Video ID for payload: ${videoIdForPayload}`);
    console.log(`🔍 [GET SAVED PROGRESS] Searching in ${courseProgressData[0].length} progress items`);

    const progressItem = courseProgressData[0].find(
      (item: any) => item?.video?.id === videoIdForPayload,
    );

    console.log(`🔍 [GET SAVED PROGRESS] Found progress item:`, progressItem);

    if (progressItem) {
      // Handle both number and string progress values
      let progress: number;
      if (typeof progressItem.progress === "number") {
        progress = progressItem.progress;
      } else if (typeof progressItem.progress === "string") {
        progress = parseFloat(progressItem.progress);
      } else {
        console.log(`❌ [GET SAVED PROGRESS] Progress is not number or string, type: ${typeof progressItem.progress}`);
        return null;
      }

      console.log(`🔍 [GET SAVED PROGRESS] Raw progress: ${progressItem.progress}, parsed: ${progress}, isNaN: ${Number.isNaN(progress)}, isFinite: ${isFinite(progress)}`);

      // Validate progress value
      if (!Number.isNaN(progress) && isFinite(progress)) {
        const finalProgress = Math.max(0, Math.min(100, progress));
        console.log(`✅ [GET SAVED PROGRESS] Returning progress: ${finalProgress}%`);
        return finalProgress;
      } else {
        console.log(`❌ [GET SAVED PROGRESS] Progress is NaN or not finite, returning null`);
      }
    } else {
      console.log(`❌ [GET SAVED PROGRESS] No progress item found for video ${videoIdForPayload}`);
    }
    return null;
  };

  const renderVideoPlayer = () => {
    let selectedVideo: any = null;
    for (const chapter of (courseChaptersData as any)?.rows || []) {
      const video = chapter?.courseVideos?.find(
        (v: any) => v?.id === selectedVideoId,
      );
      if (video) {
        selectedVideo = video;
        break;
      }
    }
    let videoUrl: string | null = null;
    if (selectedVideo) {
      if (selectedVideo?.isFree === false) {
        videoUrl = videoData?.url || null;
      } else {
        videoUrl = selectedVideo?.url || selectedVideoUrl || currentVideoUrl;
      }
    } else {
      videoUrl = selectedVideoUrl || currentVideoUrl;
    }

    // Get saved progress percentage for this video
    const savedProgress = getSavedProgress();
    console.log(`🎬 [RENDER VIDEO PLAYER] Video ID: ${selectedVideoId}, Saved Progress: ${savedProgress}%, initialProgress will be: ${savedProgress}`);

    // If no video is selected, show default thumbnail
    if (!videoUrl || !selectedVideoId) {
      return (
        <Box
          w="100%"
          position="relative"
          borderRadius="12px"
          overflow="hidden"
          display="flex"
          alignItems="center"
          justifyContent="center"
          maxH={["200px", "300px", "400px"]}
          h={["200px", "300px", "400px"]}
        >
          <Image
            src={courseData?.thumbnail || defaultThumbnail}
            alt="Course Thumbnail"
            w="100%"
            h="100%"
            objectFit="contain"
            borderRadius="12px"
          />
        </Box>
      );
    }

    // If progress is still loading, show loading state to prevent video from starting
    // This ensures we always check previous progress before allowing video to play
    if (enrolled && getCourseProgressLoading && !courseProgressData?.[0]) {
      return (
        <Box
          w="100%"
          position="relative"
          borderRadius="12px"
          overflow="hidden"
          bg="gray.100"
          display="flex"
          alignItems="center"
          justifyContent="center"
          maxH={["200px", "300px", "400px"]}
          h={["200px", "300px", "400px"]}
        >
          <Spinner size="xl" color="#1819B4" />
        </Box>
      );
    }

    // If video is selected, show video player
    // Include savedProgress in key to force re-render when progress loads
    // This ensures video seeks to correct position even if progress loads after component mounts
    return (
      <ReactVideoPlayerNew
        key={`${videoUrl}-${savedProgress !== null ? savedProgress : 'no-progress'}`}
        videoUrl={videoUrl}
        title={courseData?.maintitle || courseData?.title}
        thumbnail={courseData?.thumbnail || defaultThumbnail}
        useCredentials={!selectedVideo?.isCustomUrl}
        width="100%"
        height="100%"
        initialProgress={savedProgress}
        onPlay={async () => {
          if (!hasPlayed) {
            setHasPlayed(true);
            if (enrolled) {
              // CRITICAL: Wait for progress data to load before allowing video to play
              // This prevents the glitch where video starts from 0 before checking saved progress
              if (getCourseProgressLoading) {
                // Still loading - wait for it to finish
                // The video player will be blocked by the loading check above
                return;
              }

              // Always check for existing progress record first
              // Wait for courseProgressData to load if it hasn't loaded yet
              if (courseProgressData?.[0]) {
                // Data is loaded - check for existing progress
                const savedProgress = getSavedProgress();

                // Check if video is already completed
                let selectedVideo: any = null;
                for (const chapter of (courseChaptersData as any)?.rows || []) {
                  const video = chapter?.courseVideos?.find(
                    (v: any) => v?.id === selectedVideoId,
                  );
                  if (video) {
                    selectedVideo = video;
                    break;
                  }
                }
                const videoIdForPayload =
                  selectedVideo?.isFree === false
                    ? selectedVideo?.id
                    : selectedVideoId;

                const progressItem = courseProgressData[0].find(
                  (item: any) => item?.video?.id === videoIdForPayload,
                );

                // If video is completed, use 100% progress
                if (progressItem?.completed === true) {
                  lastSavedProgressRef.current = 100;
                  return;
                }

                if (savedProgress !== null && savedProgress !== undefined) {
                  // We have saved progress - use it, NEVER reset to 0
                  lastSavedProgressRef.current = savedProgress;
                } else {
                  // No saved progress found - check if progress record exists at all
                  const hasProgressRecord = progressItem !== undefined;

                  // Only create a new progress record with 0 if there's truly no record
                  // AND only if video is not completed
                  if (!hasProgressRecord && !progressItem?.completed) {
                    await handleVideoProgress(0, false);
                    lastSavedProgressRef.current = 0;
                  } else if (hasProgressRecord) {
                    // Record exists - use its progress, never reset to 0
                    const existingProgress = typeof progressItem?.progress === "number"
                      ? progressItem.progress
                      : 0;
                    lastSavedProgressRef.current = existingProgress;
                  }
                }
              } else {
                // courseProgressData hasn't loaded yet - refresh and wait
                if (!getCourseProgressLoading) {
                  await getCourseProgressById(courseData?.id);
                  // After refresh, check for saved progress again
                  const refreshedProgress = getSavedProgress();
                  if (refreshedProgress !== null && refreshedProgress !== undefined) {
                    // Found saved progress - use it
                    lastSavedProgressRef.current = refreshedProgress;
                  } else if (courseProgressData?.[0]) {
                    // Data loaded but no progress - check if record exists
                    let selectedVideo: any = null;
                    for (const chapter of (courseChaptersData as any)?.rows || []) {
                      const video = chapter?.courseVideos?.find(
                        (v: any) => v?.id === selectedVideoId,
                      );
                      if (video) {
                        selectedVideo = video;
                        break;
                      }
                    }
                    const videoIdForPayload =
                      selectedVideo?.isFree === false
                        ? selectedVideo?.id
                        : selectedVideoId;

                    const hasRecord = courseProgressData[0].some(
                      (item: any) => item?.video?.id === videoIdForPayload,
                    );

                    // Only create record if truly no record exists
                    if (!hasRecord) {
                      await handleVideoProgress(0, false);
                      lastSavedProgressRef.current = 0;
                    }
                  }
                }
                // If still loading, the loading check above will prevent video from playing
              }
            }
          }
        }}
        onPause={async (progress) => {
          if (enrolled && progress !== undefined) {
            // Never mark as completed on pause, only on video end
            await handleVideoProgress(progress, false);
          }
        }}
        onProgress={async (progress) => {
          // Save progress periodically while playing (every 15 seconds worth of progress)
          if (enrolled && progress !== undefined) {
            // Check if video is already completed - if so, don't update
            let selectedVideo: any = null;
            for (const chapter of (courseChaptersData as any)?.rows || []) {
              const video = chapter?.courseVideos?.find(
                (v: any) => v?.id === selectedVideoId,
              );
              if (video) {
                selectedVideo = video;
                break;
              }
            }
            const videoIdForPayload =
              selectedVideo?.isFree === false
                ? selectedVideo?.id
                : selectedVideoId;

            const progressItem = courseProgressData?.[0]?.find(
              (item: any) => item?.video?.id === videoIdForPayload,
            );

            // If video is already completed, don't update progress
            if (progressItem?.completed === true) {
              return;
            }

            // Only save if progress changed significantly (more than 5%)
            // AND progress is increasing (never decrease)
            // AND enough time has passed since last save (throttling handled in handleVideoProgress)
            if (
              lastSavedProgressRef.current === null ||
              (progress > lastSavedProgressRef.current &&
                Math.abs(progress - lastSavedProgressRef.current) >= 5)
            ) {
              // Don't await - let it run in background to avoid blocking
              handleVideoProgress(progress, false, true).catch((err) => {
                console.error("Error in onProgress handler:", err);
              });
            }
          }
        }}
        onEnded={async () => {
          if (enrolled) {
            // Mark video as completed when it ends (100% progress, completed=true)
            await handleVideoProgress(100, true);
            lastSavedProgressRef.current = 100;
          }
          // Clear interval when video ends
          if (progressSaveIntervalRef.current) {
            clearInterval(progressSaveIntervalRef.current);
            progressSaveIntervalRef.current = null;
          }
        }}
      />
    );
  };

  const handleVideoSelect = (video: any, chapterIdx: number) => {
    // Save current video progress before switching
    if (enrolled && selectedVideoId && lastSavedProgressRef.current !== null) {
      handleVideoProgress(lastSavedProgressRef.current, false).catch(
        console.error,
      );
    }

    setSelectedChapter(chapterIdx);
    setSelectedVideoUrl(video?.url || null);
    setSelectedVideoId(video?.id || null);
    setHasPlayed(false); // Reset hasPlayed when switching videos
    // Don't reset lastSavedProgressRef here - let useEffect initialize it from saved progress
    // This prevents race conditions where we might create duplicate records
    // Clear progress save interval when switching videos
    if (progressSaveIntervalRef.current) {
      clearInterval(progressSaveIntervalRef.current);
      progressSaveIntervalRef.current = null;
    }
    // Note: We keep progressIdMapRef entries when switching videos since progress IDs persist
    fetchVideoUrlById(video?.id);
  };

  const handleChapterSelect = (chapterIdx: number, firstVideo: any) => {
    // Save current video progress before switching
    if (enrolled && selectedVideoId && lastSavedProgressRef.current !== null) {
      handleVideoProgress(lastSavedProgressRef.current, false).catch(
        console.error,
      );
    }

    setSelectedChapter(chapterIdx);
    if (firstVideo) {
      setSelectedVideoUrl(firstVideo?.url || null);
      setSelectedVideoId(firstVideo?.id || null);
      setHasPlayed(false); // Reset hasPlayed when switching videos
      // Don't reset lastSavedProgressRef here - let useEffect initialize it from saved progress
      // This prevents race conditions where we might create duplicate records
      // Clear progress save interval when switching videos
      if (progressSaveIntervalRef.current) {
        clearInterval(progressSaveIntervalRef.current);
        progressSaveIntervalRef.current = null;
      }
      fetchVideoUrlById(firstVideo?.id);
    }
  };

  return (
    <Box overflowY={"auto"} pt={2} px={6} pb={8}>
      {!isMobile && (
        <Button
          width={"fit-content"}
          variant="link"
          mb={4}
          onClick={() => navigate(-1)}
        >
          &larr; {t("courseDetail.goBack")}
        </Button>
      )}
      {/* Video Player - always visible */}
      {renderVideoPlayer()}
      {/* Header - always visible */}
      <CourseHeader
        courseData={courseData}
        enrolled={enrolled}
        addCourseEnrollLoading={addCourseEnrollLoading}
        watchLaterLoading={watchLaterLoading}
        isCourseExpired={isCourseExpired}
        onEnroll={handleEnroll}
        onWatchLater={handleWatchLater}
        onUndoWatchLater={handleUndoWatchLater}
        onUpdateWatchLater={handleUpdateWatchLater}
      />

      {/* Content based on enrollment status */}
      {enrolled ? (
        <ChaptersList
          chapters={chapters}
          selectedChapter={selectedChapter}
          enrolled={enrolled}
          courseProgressData={courseProgressData}
          onChapterSelect={handleChapterSelect}
          onVideoSelect={handleVideoSelect}
        />
      ) : (
        <Box mt={6}>
          <EnrollmentList
            courses={coursesData?.rows}
            loading={recommendedCoursesLoading}
          />
        </Box>
      )}

      {!isMobileTour && !journalistCourseDetailTourGuide.isActive && (
        <Portal>
          <RestartTourButton
            onClick={journalistCourseDetailTourGuide.startTour}
            position={{ bottom: "24px", right: "24px" }}
          />
        </Portal>
      )}

      {!isMobileTour &&
        journalistCourseDetailTourGuide.isActive &&
        journalistCourseDetailTourGuide.currentStepData && (
          <TourGuide
            isActive={journalistCourseDetailTourGuide.isActive}
            currentStep={journalistCourseDetailTourGuide.currentStepData}
            currentStepIndex={journalistCourseDetailTourGuide.currentStep}
            totalSteps={journalistCourseDetailTourGuide.totalSteps}
            onNext={journalistCourseDetailTourGuide.nextStep}
            onBack={journalistCourseDetailTourGuide.previousStep}
            onClose={journalistCourseDetailTourGuide.stopTour}
            targetSelector={
              journalistCourseDetailTourGuide.currentStepData.targetSelector
            }
            borderRadius="8px"
          />
        )}
    </Box>
  );
};

export default CourseDetail;
